import { useState, useEffect } from "react";
import { useHLAuth } from "@/context/HLAuthContext";
import { useNavigate } from "react-router-dom";
import { Search, LogOut, Loader2, Star, MapPin, Clock, Video, BookmarkPlus, BookmarkCheck, X, Building2, ChevronDown } from "lucide-react";

function VideoEmbed({ url }) {
  if (!url) return null;
  const src = url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/").replace("loom.com/share/", "loom.com/embed/");
  return <iframe src={src} title="Pitch" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" allowFullScreen />;
}

function AspirantCard({ a, isShortlisted, onToggleShortlist, onView }) {
  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-5 hover:border-emerald-500/20 transition-all cursor-pointer" onClick={onView} data-testid={`aspirant-card-${a.user_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-base font-semibold text-white">{a.name}</h3>
          <p className="text-xs text-slate-500">{a.headline || a.current_role}</p>
        </div>
        <button onClick={e => { e.stopPropagation(); onToggleShortlist(); }} className={`p-1.5 rounded-lg transition-all ${isShortlisted ? "text-emerald-400 bg-emerald-500/10" : "text-slate-600 hover:text-emerald-400"}`} data-testid={`shortlist-${a.user_id}`}>
          {isShortlisted ? <BookmarkCheck size={16} /> : <BookmarkPlus size={16} />}
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(a.skills || []).slice(0, 4).map(s => <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">{s}</span>)}
        {(a.skills || []).length > 4 && <span className="text-[10px] text-slate-600">+{a.skills.length - 4} more</span>}
      </div>
      <div className="flex items-center gap-4 text-[11px] text-slate-500">
        {a.experience_years > 0 && <span><Clock size={10} className="inline mr-1" />{a.experience_years} yrs</span>}
        {a.location && <span><MapPin size={10} className="inline mr-1" />{a.location}</span>}
        {a.video_url && <span className="text-emerald-400"><Video size={10} className="inline mr-1" />Has Video</span>}
      </div>
    </div>
  );
}

function AspirantDetail({ a, onClose, isShortlisted, onToggleShortlist }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-6" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-3xl w-full p-8 relative" onClick={e => e.stopPropagation()} data-testid="aspirant-detail">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white"><X size={18} /></button>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{a.name}</h2>
            <p className="text-sm text-slate-400 mt-1">{a.headline || a.current_role}</p>
          </div>
          <button onClick={onToggleShortlist} className={`px-4 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 transition-all ${isShortlisted ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-slate-800 text-slate-400 hover:text-emerald-400"}`}>
            {isShortlisted ? <><BookmarkCheck size={14} /> Shortlisted</> : <><BookmarkPlus size={14} /> Shortlist</>}
          </button>
        </div>

        {/* Video */}
        {a.video_url && (
          <div className="aspect-video rounded-xl overflow-hidden bg-slate-800 mb-6">
            <VideoEmbed url={a.video_url} />
          </div>
        )}

        {/* Info grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Experience", value: `${a.experience_years || 0} years` },
            { label: "Location", value: a.location || "—" },
            { label: "Availability", value: a.availability || "—" },
            { label: "Salary Exp.", value: a.salary_expectation || "—" },
          ].map(f => (
            <div key={f.label} className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-[9px] text-slate-500 uppercase">{f.label}</p>
              <p className="text-xs font-medium text-slate-200 mt-0.5">{f.value}</p>
            </div>
          ))}
        </div>

        {/* Skills */}
        {a.skills?.length > 0 && (
          <div className="mb-6">
            <p className="text-xs text-slate-500 uppercase mb-2">Skills</p>
            <div className="flex flex-wrap gap-1.5">{a.skills.map(s => <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{s}</span>)}</div>
          </div>
        )}

        {/* Education */}
        {a.education && <div className="mb-6"><p className="text-xs text-slate-500 uppercase mb-1">Education</p><p className="text-sm text-slate-300">{a.education}</p></div>}

        {/* 3 Questions */}
        <div className="space-y-4">
          {[
            { q: "End aspiration / goal", a: a.q_aspiration },
            { q: "Skills beyond corporate role", a: a.q_skills_outside },
            { q: "Why hire me?", a: a.q_why_hire },
          ].filter(item => item.a).map(item => (
            <div key={item.q} className="bg-slate-800/30 rounded-xl p-4">
              <p className="text-[10px] text-emerald-400 uppercase tracking-wider mb-1">{item.q}</p>
              <p className="text-sm text-slate-300 leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function HLCorporateDashboard() {
  const { user, logout, api } = useHLAuth();
  const navigate = useNavigate();
  const [aspirants, setAspirants] = useState([]);
  const [shortlisted, setShortlisted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ skills: "", experience_min: 0, experience_max: 15, location: "" });
  const [tab, setTab] = useState("browse");

  useEffect(() => {
    Promise.all([
      api.get("/corporate/browse").then(r => setAspirants(r.data)),
      api.get("/corporate/profile").then(r => setShortlisted(r.data.shortlisted || [])),
    ]).finally(() => setLoading(false));
  }, []);

  const searchAspirants = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.skills) params.append("skills", filters.skills);
      if (filters.experience_min > 0) params.append("experience_min", filters.experience_min);
      if (filters.experience_max < 15) params.append("experience_max", filters.experience_max);
      if (filters.location) params.append("location", filters.location);
      const r = await api.get(`/corporate/browse?${params}`);
      setAspirants(r.data);
    } catch {} finally { setLoading(false); }
  };

  const toggleShortlist = async (userId) => {
    const r = await api.post(`/corporate/shortlist/${userId}`);
    setShortlisted(r.data.shortlisted);
  };

  const handleLogout = () => { logout(); navigate("/humanloop/corporate"); };

  const displayList = tab === "shortlisted" ? aspirants.filter(a => shortlisted.includes(a.user_id)) : aspirants;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="hl-corporate-dashboard">
      <header className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-400" />
            <span className="text-sm font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Talent Discovery</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">{user?.email}</span>
            <button onClick={handleLogout} className="text-xs text-slate-400 hover:text-red-400 flex items-center gap-1" data-testid="hl-corp-logout"><LogOut size={12} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6">
          <button onClick={() => setTab("browse")} className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${tab === "browse" ? "bg-emerald-500 text-slate-950" : "bg-slate-900 text-slate-400 border border-slate-800"}`} data-testid="hl-tab-browse">Browse All</button>
          <button onClick={() => setTab("shortlisted")} className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${tab === "shortlisted" ? "bg-emerald-500 text-slate-950" : "bg-slate-900 text-slate-400 border border-slate-800"}`} data-testid="hl-tab-shortlisted">Shortlisted ({shortlisted.length})</button>
        </div>

        {/* Filters */}
        {tab === "browse" && (
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[150px]"><label className="text-[10px] text-slate-500 mb-1 block">Skills</label><input className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 focus:border-emerald-500 outline-none" value={filters.skills} onChange={e => setFilters({...filters, skills: e.target.value})} placeholder="Python, Sales, Marketing..." /></div>
            <div className="w-28"><label className="text-[10px] text-slate-500 mb-1 block">Exp Min</label><input type="number" min="0" max="15" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 focus:border-emerald-500 outline-none" value={filters.experience_min} onChange={e => setFilters({...filters, experience_min: parseInt(e.target.value) || 0})} /></div>
            <div className="w-28"><label className="text-[10px] text-slate-500 mb-1 block">Exp Max</label><input type="number" min="0" max="15" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 focus:border-emerald-500 outline-none" value={filters.experience_max} onChange={e => setFilters({...filters, experience_max: parseInt(e.target.value) || 15})} /></div>
            <div className="flex-1 min-w-[120px]"><label className="text-[10px] text-slate-500 mb-1 block">Location</label><input className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-200 focus:border-emerald-500 outline-none" value={filters.location} onChange={e => setFilters({...filters, location: e.target.value})} placeholder="Patna, Bengaluru..." /></div>
            <button onClick={searchAspirants} className="bg-emerald-500 text-slate-950 px-5 py-2 rounded-lg text-xs font-bold hover:bg-emerald-400 transition-all flex items-center gap-1.5" data-testid="hl-search-btn"><Search size={13} /> Search</button>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>
        ) : displayList.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-500">{tab === "shortlisted" ? "No shortlisted candidates yet" : "No matching profiles found"}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayList.map(a => (
              <AspirantCard key={a.user_id} a={a} isShortlisted={shortlisted.includes(a.user_id)} onToggleShortlist={() => toggleShortlist(a.user_id)} onView={() => setSelected(a)} />
            ))}
          </div>
        )}

        <p className="text-center text-xs text-slate-600 mt-8">{displayList.length} profile{displayList.length !== 1 ? "s" : ""} found</p>

        {/* Detail modal */}
        {selected && <AspirantDetail a={selected} onClose={() => setSelected(null)} isShortlisted={shortlisted.includes(selected.user_id)} onToggleShortlist={() => toggleShortlist(selected.user_id)} />}
      </main>
    </div>
  );
}
